<template>
  <div class="p-6">
    <h1 class="text-2xl font-medium text-gray-800 mb-8">Change Password</h1>

    <div class="flex items-start gap-3 p-4 bg-white border border-orange-200 rounded-lg mb-8">
      <div class="flex items-center justify-center flex-shrink-0 mt-0.5">
        <component :is="Info" class="w-6 h-6 text-primary"/>
      </div>
      <p class="text-gray-600 text-sm leading-relaxed">
        Enter your current password and choose a new password containing at least 8 characters, including uppercase,
        number, lowercase, and special character.
      </p>
    </div>

    <!-- Steps -->
    <div
        class="flex md:flex-row md:justify-between md:items-center md:border-b md:border-gray-200 mb-8 flex-col items-start gap-5">
      <div class="w-fit bg-gray-100 transform translate-y-1/2 px-2 flex items-center justify-center relative">
        <div
            :class="['w-8 h-8 flex items-center justify-center rounded-full font-bold z-10', step === 1 ? 'bg-gray-800 text-white' : 'bg-gray-200 text-gray-700']">
          1
        </div>
        <span :class="['ml-2 text-gray-500', { 'font-medium text-gray-900': step === 1 }]">Fill Data</span>
      </div>

      <div class="w-fit bg-gray-100 transform translate-y-1/2 px-2 flex items-center justify-center relative">
        <div
            :class="['w-8 h-8 flex items-center justify-center rounded-full z-10', step === 2 ? 'bg-gray-800 text-white' : 'bg-gray-200 text-gray-700']">
          2
        </div>
        <span :class="['ml-2 text-gray-500', { 'font-medium text-gray-900': step === 2 }]">Enter OTP</span>
      </div>

      <div class="w-fit bg-gray-100 transform translate-y-1/2 px-2 flex items-center justify-center relative">
        <div
            :class="['w-8 h-8 flex items-center justify-center bg-gray-200 text-gray-700 rounded-full z-10', step === 3 ? 'bg-gray-800 text-white' : 'bg-gray-200 text-gray-700']">
          3
        </div>
        <span :class="['ml-2 text-gray-500', { 'font-medium text-gray-900': step === 3 }]">Confirmation</span>
      </div>
    </div>

    <div class="space-y-6">
      <!-- Step 1: Enter Current & New Password -->
      <div v-if="step === 1">
        <!-- Current Password -->
        <div class="relative">
          <input
              v-model="currentPassword"
              :type="showCurrentPassword ? 'text' : 'password'"
              placeholder="Current password *"
              class="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent placeholder-gray-500"
          />
          <button @click="showCurrentPassword = !showCurrentPassword" type="button"
                  class="absolute right-4 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600">
            <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path v-if="!showCurrentPassword" stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                    d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"/>
              <path v-if="!showCurrentPassword" stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                    d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"/>
              <path v-if="showCurrentPassword" stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                    d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.878 9.878L3 3m6.878 6.878L21 21"/>
            </svg>
          </button>
        </div>
        <p v-if="errors.currentPassword" class="text-red-500 text-sm mt-1">{{ errors.currentPassword }}</p>

        <!-- New Password -->
        <div class="relative mt-4">
          <input
              v-model="newPassword"
              :type="showNewPassword ? 'text' : 'password'"
              placeholder="New password *"
              class="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent placeholder-gray-500"
          />
          <button @click="showNewPassword = !showNewPassword" type="button"
                  class="absolute right-4 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600">
            <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path v-if="!showNewPassword" stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                    d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"/>
              <path v-if="!showNewPassword" stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                    d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"/>
              <path v-if="showNewPassword" stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                    d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.878 9.878L3 3m6.878 6.878L21 21"/>
            </svg>
          </button>
        </div>

        <!-- Password Validation Messages -->
        <ul class="text-red-500 text-sm mt-1 space-y-1">
          <li v-if="errors.newPasswordLength">{{ errors.newPasswordLength }}</li>
          <li v-if="errors.newPasswordUpper">{{ errors.newPasswordUpper }}</li>
          <li v-if="errors.newPasswordLower">{{ errors.newPasswordLower }}</li>
          <li v-if="errors.newPasswordNumber">{{ errors.newPasswordNumber }}</li>
          <li v-if="errors.newPasswordSpecial">{{ errors.newPasswordSpecial }}</li>
        </ul>

        <!-- Confirm Password -->
        <div class="relative mt-4">
          <input
              v-model="confirmPassword"
              :type="showConfirmPassword ? 'text' : 'password'"
              placeholder="Confirm new password *"
              class="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent placeholder-gray-500"
          />
        </div>
        <p v-if="errors.confirmPassword" class="text-red-500 text-sm mt-1">{{ errors.confirmPassword }}</p>

        <!--        <div class="flex justify-center mt-6">-->
        <!--          <button @click.prevent="requestOtp" class="bg-primary text-white font-medium py-3 px-12 rounded-lg transition-colors duration-200">Request OTP</button>-->
        <!--        </div>-->

        <div class="flex justify-center mt-6">
          <button
              @click.prevent="requestOtp"
              :disabled="isRequesting"
              class="bg-primary text-white font-medium py-3 px-12 rounded-lg transition-colors duration-200 flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <template v-if="!isRequesting">
              Request OTP
            </template>
            <template v-else>
              <svg class="animate-spin h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none"
                   viewBox="0 0 24 24">
                <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                <path class="opacity-75" fill="currentColor"
                      d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z"></path>
              </svg>
              Sending...
            </template>
          </button>
        </div>

      </div>

      <!-- Step 2: Enter OTP -->
      <div v-else-if="step === 2">
        <input
            v-model="otp"
            type="text"
            placeholder="Enter OTP"
            class="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent placeholder-gray-500"
        />
        <!--        <div class="flex justify-center mt-6">-->
        <!--          <button @click.prevent="verifyOtp" class="bg-primary text-white font-medium py-3 px-12 rounded-lg transition-colors duration-200">Confirm OTP</button>-->
        <!--        </div>-->

        <div class="flex justify-center mt-6">
          <button
              @click.prevent="verifyOtp"
              :disabled="isVerifying"
              class="bg-primary text-white font-medium py-3 px-12 rounded-lg transition-colors duration-200 flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <template v-if="!isVerifying">
              Confirm OTP
            </template>
            <template v-else>
              <svg class="animate-spin h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none"
                   viewBox="0 0 24 24">
                <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                <path class="opacity-75" fill="currentColor"
                      d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z"></path>
              </svg>
              Verifying...
            </template>
          </button>
        </div>


      </div>

      <!-- Step 3: Confirmation -->
      <div v-else class="text-green-600 font-semibold text-center">
        Password changed successfully!
      </div>
    </div>
  </div>
</template>

<script setup>
import {ref, reactive} from 'vue'
import {Info} from 'lucide-vue-next'
import api from '@/services/api'

const step = ref(1)
const currentPassword = ref('')
const newPassword = ref('')
const confirmPassword = ref('')
const otp = ref('')

const showCurrentPassword = ref(false)
const showNewPassword = ref(false)
const showConfirmPassword = ref(false)
const isRequesting = ref(false)
const isVerifying = ref(false)


const errors = reactive({
  currentPassword: '',
  newPasswordLength: '',
  newPasswordUpper: '',
  newPasswordLower: '',
  newPasswordNumber: '',
  newPasswordSpecial: '',
  confirmPassword: ''
})

const validatePassword = () => {
  errors.currentPassword = currentPassword.value ? '' : 'Current password is required.'
  errors.newPasswordLength = newPassword.value.length < 8 ? 'Password must be at least 8 characters.' : ''
  errors.newPasswordUpper = !/[A-Z]/.test(newPassword.value) ? 'Password must include an uppercase letter.' : ''
  errors.newPasswordLower = !/[a-z]/.test(newPassword.value) ? 'Password must include a lowercase letter.' : ''
  errors.newPasswordNumber = !/[0-9]/.test(newPassword.value) ? 'Password must include a number.' : ''
  errors.newPasswordSpecial = !/[!@#$%^&*?]/.test(newPassword.value) ? 'Password must include a special character (!@#$%^&*?).' : ''
  errors.confirmPassword = confirmPassword.value !== newPassword.value ? 'Passwords do not match.' : ''
  return !errors.currentPassword && !errors.newPasswordLength && !errors.newPasswordUpper &&
      !errors.newPasswordLower && !errors.newPasswordNumber && !errors.newPasswordSpecial &&
      !errors.confirmPassword
}

const requestOtp = async () => {
  if (!validatePassword()) return
  if (isRequesting.value) return

  isRequesting.value = true
  try {
    await api.post('/password-change/request', {
      current_password: currentPassword.value,
      new_password: newPassword.value,
      confirm_password: confirmPassword.value
    }, {headers: {Authorization: `Bearer ${localStorage.getItem('token')}`}})

    step.value = 2
  } catch (err) {
    if (err.response?.data?.message) {
      errors.currentPassword = err.response.data.message
    } else {
      errors.currentPassword = 'Error requesting OTP'
    }
  } finally {
    isRequesting.value = false
  }
}

const verifyOtp = async () => {
  if (isVerifying.value) return
  isVerifying.value = true

  try {
    await api.post('/password-change/confirm', {
      otp: otp.value,
      new_password: newPassword.value,
      current_password: currentPassword.value,
      confirm_password: confirmPassword.value
    }, {headers: {Authorization: `Bearer ${localStorage.getItem('token')}`}})

    step.value = 3
  } catch (err) {
    // خطای سرور
  } finally {
    isVerifying.value = false
  }
}

</script>
